"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AnimatedSection from "./AnimatedSection";
import MatIcon from "./ui/MatIcon";

interface Story {
  tag: string;
  title: string;
  date: string;
  href: string;
  external?: boolean;
  image?: string;
  imageAlt?: string;
  /* Material symbol used for the branded placeholder when no photo exists */
  icon?: string;
}

const stories: Story[] = [
  {
    tag: "Feature",
    title: "Meet Arya: Bold Journey Magazine Interviews Our Founder",
    date: "Apr 2026",
    href: "https://boldjourney.com/meet-arya/",
    external: true,
    image: "/featured/bold-journey.jpg",
    imageAlt: "Arya Devanath, founder of Memory Matters",
  },
  {
    tag: "Presentation",
    title:
      "The Defiance Presented at the Global Innovation Field Trip Celebration",
    date: "Apr 18–19, 2026",
    href: "https://youtu.be/rga5s6nka0c",
    external: true,
    image: "/featured/defiance-gift.jpg",
    imageAlt: "The Defiance by Arya Devanath presentation",
  },
  {
    tag: "Feature",
    title:
      "How This Youth-Led Initiative is Changing Dementia Care — The Professor Kev Show",
    date: "Feb 2026",
    href: "https://youtu.be/m-j87WpSt2Q",
    external: true,
    image: "/featured/professor-kev.jpg",
    imageAlt: "Memory Matters featured on The Professor Kev Show",
  },
  {
    tag: "Feature",
    title: "Eunoia Global Health Features Memory Matters",
    date: "Feb 2026",
    href: "https://www.instagram.com/eunoiaglobalhealth/p/DUwJOmaErdv/",
    external: true,
    image: "/featured/eunoia.jpg",
    imageAlt: "Eunoia Global Health podcast episode with Arya Devanath",
  },
  {
    tag: "Feature",
    title: "Meet MSKTC Ambassador Arya Devanath",
    date: "Feb 2026",
    href: "https://msktc.org/tbi/ambassador-program/directory/arya-devanath",
    external: true,
    image: "/featured/msktc.jpg",
    imageAlt: "Arya Devanath, MSKTC Ambassador",
  },
  {
    tag: "Award",
    title: "20 Under 20 Young Innovators Honoree — Innovation Insider",
    date: "Jan 2026",
    href: "https://innovationworld.org/innovation-insider/",
    external: true,
    image: "/featured/innovation-insider.jpg",
    imageAlt: "20 Under 20 Honoree feature on Arya Devanath",
  },
  {
    tag: "Talk",
    title: "The Brain's Memory Dust — Ignite Talk by Arya Devanath",
    date: "Dec 2025",
    href: "https://youtu.be/YIEmfSdA9lQ",
    external: true,
    image: "/featured/ignite-talk.jpg",
    imageAlt: "The Brain's Memory Dust Ignite Talk",
  },
  {
    tag: "Community",
    title: "Memory Matters on Instagram: Neuroscience Made Accessible",
    date: "Ongoing",
    href: "https://www.instagram.com/memorymatters.official/",
    external: true,
    image: "/featured/mm-instagram.jpg",
    imageAlt: "Start a chapter with Memory Matters",
  },
  {
    tag: "Event",
    title: "Mind Tricks: The Magic of Neuroscience — Interactive Chapter Show",
    date: "2025",
    href: "https://www.instagram.com/mind.tricks_official/p/DDdjClrxGZs/",
    external: true,
    image: "/featured/mind-tricks.jpg",
    imageAlt: "Mind Tricks: The Magic of Neuroscience live show",
  },
];

export default function TopStoriesSection() {
  const railRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  /* Track how far the rail is scrolled so the arrows can disable at each end. */
  const syncArrows = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    setAtStart(rail.scrollLeft <= 1);
    setAtEnd(rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 1);
  }, []);

  useEffect(() => {
    syncArrows();
    window.addEventListener("resize", syncArrows);
    return () => window.removeEventListener("resize", syncArrows);
  }, [syncArrows]);

  /* Advance by one card, falling back to a viewport-width nudge. */
  const scrollByCard = (direction: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.querySelector<HTMLElement>("[data-story-card]");
    const step = card ? card.offsetWidth + 32 : rail.clientWidth;
    rail.scrollBy({ left: step * direction, behavior: "smooth" });
  };

  return (
    <section data-component-id="src/components/TopStoriesSection.tsx:133:4" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="133" data-component-end-line="279" data-component-file="TopStoriesSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22stories%22%2C%22className%22%3A%22bg-%5B%23291533%5D%20relative%20overflow-hidden%22%7D" id="stories" className="bg-[#291533] relative overflow-hidden" style={{ padding: "100px 0" }}>
      {/* Background texture */}
      <div data-component-id="src/components/TopStoriesSection.tsx:135:6" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="135" data-component-end-line="147" data-component-file="TopStoriesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20pointer-events-none%20opacity-10%22%7D"
        className="absolute pointer-events-none opacity-10"
        style={{
          width: "984px",
          height: "282px",
          top: "-120px",
          right: "-80px",
          background:
            "repeating-linear-gradient(45deg, #9a5999 0, #9a5999 1px, transparent 0, transparent 50%)",
          backgroundSize: "8px 8px",
          transform: "rotate(15deg)",
        }}
      />

      <div data-component-id="src/components/TopStoriesSection.tsx:149:6" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="149" data-component-end-line="278" data-component-file="TopStoriesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-%5B1460px%5D%20mx-auto%20px-8%20relative%20z-10%22%7D" className="max-w-[1460px] mx-auto px-8 relative z-10">
        {/* Header */}
        <AnimatedSection data-component-id="src/components/TopStoriesSection.tsx:151:8" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="151" data-component-end-line="190" data-component-file="TopStoriesSection.tsx" data-component-name="AnimatedSection" data-component-content="%7B%22elementName%22%3A%22AnimatedSection%22%7D" delay={0}>
          <div data-component-id="src/components/TopStoriesSection.tsx:152:10" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="152" data-component-end-line="189" data-component-file="TopStoriesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-6%20mb-14%22%7D" className="flex items-center gap-6 mb-14">
            <h2 data-component-id="src/components/TopStoriesSection.tsx:153:12" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="153" data-component-end-line="158" data-component-file="TopStoriesSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-geist%20font-semibold%20text-white%20leading-%5B1.1em%5D%20whitespace-nowrap%22%2C%22textContent%22%3A%22Top%20Stories%22%7D"
              className="font-geist font-semibold text-white leading-[1.1em] whitespace-nowrap"
              style={{ fontSize: "clamp(28px, 3vw, 44px)" }}
            >
              Top Stories
            </h2>
            <div data-component-id="src/components/TopStoriesSection.tsx:159:12" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="159" data-component-end-line="159" data-component-file="TopStoriesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-px%20flex-1%20bg-white%2F25%22%7D" className="h-px flex-1 bg-white/25" />

            {/* Rail controls */}
            <div data-component-id="src/components/TopStoriesSection.tsx:162:12" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="162" data-component-end-line="188" data-component-file="TopStoriesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20flex-shrink-0%22%7D" className="flex items-center gap-3 flex-shrink-0">
              {(
                [
                  { dir: -1 as const, label: "Previous stories", path: "M15 5l-7 7 7 7", disabled: atStart },
                  { dir: 1 as const, label: "Next stories", path: "M9 5l7 7-7 7", disabled: atEnd },
                ]
              ).map((btn) => (
                <button data-component-id="src/components/TopStoriesSection.tsx:169:16" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="169" data-component-end-line="186" data-component-file="TopStoriesSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22w-11%20h-11%20rounded-full%20border%20border-white%2F25%20flex%20items-center%20justify-center%20text-white%20transition-colors%20duration-200%20hover%3Aborder-%5B%23ffd166%5D%20hover%3Atext-%5B%23ffd166%5D%20focus-visible%3Aoutline%20focus-visible%3Aoutline-2%20focus-visible%3Aoutline-offset-2%20focus-visible%3Aoutline-%5B%23ffd166%5D%20disabled%3Aopacity-30%20disabled%3Apointer-events-none%22%7D"
                  key={btn.label}
                  type="button"
                  onClick={() => scrollByCard(btn.dir)}
                  disabled={btn.disabled}
                  aria-label={btn.label}
                  className="w-11 h-11 rounded-full border border-white/25 flex items-center justify-center text-white transition-colors duration-200 hover:border-[#ffd166] hover:text-[#ffd166] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffd166] disabled:opacity-30 disabled:pointer-events-none"
                >
                  <svg data-component-id="src/components/TopStoriesSection.tsx:177:18" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="177" data-component-end-line="185" data-component-file="TopStoriesSection.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path data-component-id="src/components/TopStoriesSection.tsx:178:20" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="178" data-component-end-line="184" data-component-file="TopStoriesSection.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                      d={btn.path}
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {/* Story rail — one row, scrolls horizontally */}
        <div data-component-id="src/components/TopStoriesSection.tsx:193:8" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="193" data-component-end-line="277" data-component-file="TopStoriesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rail%20flex%20gap-8%20overflow-x-auto%20snap-x%20snap-mandatory%20scroll-smooth%20pb-2%22%7D"
          ref={railRef}
          onScroll={syncArrows}
          className="rail flex gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2"
        >
          {stories.map((story, i) => (
            <div data-component-id="src/components/TopStoriesSection.tsx:199:12" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="199" data-component-end-line="275" data-component-file="TopStoriesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22snap-start%20flex-shrink-0%20w-%5B300px%5D%20sm%3Aw-%5B360px%5D%20lg%3Aw-%5B420px%5D%22%7D"
              key={i}
              data-story-card
              className="snap-start flex-shrink-0 w-[300px] sm:w-[360px] lg:w-[420px]"
            >
              <a data-component-id="src/components/TopStoriesSection.tsx:204:14" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="204" data-component-end-line="274" data-component-file="TopStoriesSection.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22className%22%3A%22group%20flex%20flex-col%20h-full%22%7D"
                href={story.href}
                target={story.external ? "_blank" : undefined}
                rel={story.external ? "noopener noreferrer" : undefined}
                className="group flex flex-col h-full"
              >
                {/* Image / placeholder */}
                <div data-component-id="src/components/TopStoriesSection.tsx:211:16" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="211" data-component-end-line="258" data-component-file="TopStoriesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20rounded-2xl%20overflow-hidden%20border%20border-white%2F10%20transition-all%20duration-300%20group-hover%3Aborder-%5B%23ffd166%5D%2F60%22%7D"
                  className="relative rounded-2xl overflow-hidden border border-white/10 transition-all duration-300 group-hover:border-[#ffd166]/60"
                  style={{ aspectRatio: "1.55" }}
                >
                  {story.image ? (
                    <img data-component-id="src/components/TopStoriesSection.tsx:216:20" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="216" data-component-end-line="221" data-component-file="TopStoriesSection.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22w-full%20h-full%20object-cover%20transition-transform%20duration-500%20group-hover%3Ascale-%5B1.04%5D%22%7D"
                      src={story.image}
                      alt={story.imageAlt ?? story.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  ) : (
                    <div data-component-id="src/components/TopStoriesSection.tsx:223:20" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="223" data-component-end-line="243" data-component-file="TopStoriesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20h-full%20flex%20items-center%20justify-center%20transition-transform%20duration-500%20group-hover%3Ascale-%5B1.04%5D%22%7D"
                      className="w-full h-full flex items-center justify-center transition-transform duration-500 group-hover:scale-[1.04]"
                      style={{
                        background: "linear-gradient(135deg, #9a5999, #3a1e47)",
                      }}
                    >
                      <div data-component-id="src/components/TopStoriesSection.tsx:229:22" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="229" data-component-end-line="236" data-component-file="TopStoriesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20opacity-10%22%7D"
                        className="absolute inset-0 opacity-10"
                        style={{
                          background:
                            "repeating-linear-gradient(45deg, #ffd166 0, #ffd166 1px, transparent 0, transparent 50%)",
                          backgroundSize: "8px 8px",
                        }}
                      />
                      <MatIcon data-component-id="src/components/TopStoriesSection.tsx:237:22" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="237" data-component-end-line="242" data-component-file="TopStoriesSection.tsx" data-component-name="MatIcon" data-component-content="%7B%22elementName%22%3A%22MatIcon%22%2C%22className%22%3A%22text-%5B%23ffd166%5D%20relative%22%7D"
                        name={story.icon ?? "auto_awesome"}
                        size={64}
                        fill
                        className="text-[#ffd166] relative"
                      />
                    </div>
                  )}
                  {story.external && (
                    <div data-component-id="src/components/TopStoriesSection.tsx:246:20" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="246" data-component-end-line="256" data-component-file="TopStoriesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-4%20right-4%20w-8%20h-8%20rounded-full%20bg-%5B%23291533%5D%2F70%20backdrop-blur-sm%20flex%20items-center%20justify-center%20opacity-0%20group-hover%3Aopacity-100%20transition-opacity%20duration-300%22%7D" className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#291533]/70 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <svg data-component-id="src/components/TopStoriesSection.tsx:247:22" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="247" data-component-end-line="255" data-component-file="TopStoriesSection.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path data-component-id="src/components/TopStoriesSection.tsx:248:24" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="248" data-component-end-line="254" data-component-file="TopStoriesSection.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
                          d="M3 9L9 3M9 3H4M9 3v5"
                          stroke="#ffd166"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Title */}
                <h3 data-component-id="src/components/TopStoriesSection.tsx:261:16" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="261" data-component-end-line="263" data-component-file="TopStoriesSection.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-geist%20font-semibold%20text-white%20text-%5B20px%5D%20leading-%5B1.3em%5D%20mt-5%20flex-1%20group-hover%3Atext-%5B%23ffd166%5D%20transition-colors%20duration-300%22%7D" className="font-geist font-semibold text-white text-[20px] leading-[1.3em] mt-5 flex-1 group-hover:text-[#ffd166] transition-colors duration-300">
                  {story.title}
                </h3>

                {/* Tag + date */}
                <div data-component-id="src/components/TopStoriesSection.tsx:266:16" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="266" data-component-end-line="273" data-component-file="TopStoriesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mt-4%20pt-3%20border-t%20border-white%2F10%22%7D" className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                  <span data-component-id="src/components/TopStoriesSection.tsx:267:18" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="267" data-component-end-line="269" data-component-file="TopStoriesSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-satoshi%20font-semibold%20text-white%2F60%20text-xs%20%20tracking-%5B0.08em%5D%22%7D" className="font-satoshi font-semibold text-white/60 text-xs  tracking-[0.08em]">
                    {story.tag}
                  </span>
                  <span data-component-id="src/components/TopStoriesSection.tsx:270:18" data-component-path="src/components/TopStoriesSection.tsx" data-component-line="270" data-component-end-line="272" data-component-file="TopStoriesSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-satoshi%20font-medium%20text-white%2F60%20text-sm%22%7D" className="font-satoshi font-medium text-white/60 text-sm">
                    {story.date}
                  </span>
                </div>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
